import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConsentAgreement } from '../types';

interface Props {
  isOpen: boolean;
  onSubmit: (consent: ConsentAgreement) => void;
  onClose: () => void;
}

export const ConsentModal = ({ isOpen, onSubmit, onClose }: Props) => {
  const [step, setStep] = useState(1);
  const [consent, setConsent] = useState<ConsentAgreement>({
    established: false,
    safeWord: '',
    boundaries: [],
    maxIntensity: 15,
    maxDenialDuration: 5,
    checkInInterval: 10
  });

  const boundaryOptions = [
    { id: 'intensity-capped', label: 'Cap intensity at 10 (gentler play)' },
    { id: 'no-pulse', label: 'Avoid pulsing patterns' },
    { id: 'no-denial', label: 'No orgasm denial games' },
    { id: 'check-in-5', label: 'Check in every 5 minutes' },
    { id: 'no-patterns', label: 'Vibration only, no patterns' }
  ];

  const handleSubmit = () => {
    if (consent.safeWord.length < 3) {
      alert('Safe word must be at least 3 characters');
      return;
    }
    onSubmit({ ...consent, established: true });
  };

  const toggleBoundary = (id: string) => {
    setConsent(prev => ({
      ...prev,
      boundaries: prev.boundaries.includes(id)
        ? prev.boundaries.filter(b => b !== id)
        : [...prev.boundaries, id]
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--border-radius)',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <h2 style={{ marginBottom: '0.5rem' }}>Establish Consent</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
              Before playing, establish boundaries and a safe word. Either partner can use the safe word at any time to immediately stop all activity.
            </p>

            {step === 1 && (
              <div>
                <div className="form-group">
                  <label className="form-label">Safe Word *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={consent.safeWord}
                    onChange={(e) => setConsent(prev => ({ ...prev, safeWord: e.target.value.toUpperCase() }))}
                    placeholder="e.g., RED"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Choose something easy to remember and say
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">Maximum Intensity (1-20)</label>
                  <input
                    type="range"
                    className="intensity-slider"
                    min="1"
                    max="20"
                    value={consent.maxIntensity}
                    onChange={(e) => setConsent(prev => ({ ...prev, maxIntensity: parseInt(e.target.value) }))}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                    <span>Gentle</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>{consent.maxIntensity}</span>
                    <span>Intense</span>
                  </div>
                </div>

                <button className="btn btn-primary btn-lg" onClick={() => setStep(2)} style={{ width: '100%' }}>
                  Continue
                </button>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="form-group">
                  <label className="form-label">Boundaries & Preferences</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {boundaryOptions.map((option) => (
                      <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={consent.boundaries.includes(option.id)}
                          onChange={() => toggleBoundary(option.id)}
                          style={{ width: '20px', height: '20px' }}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Max Denial Duration (minutes)</label>
                  <select
                    className="form-input"
                    value={consent.maxDenialDuration}
                    onChange={(e) => setConsent(prev => ({ ...prev, maxDenialDuration: parseInt(e.target.value) }))}
                  >
                    <option value={1}>1 minute</option>
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Check-in Interval (minutes)</label>
                  <select
                    className="form-input"
                    value={consent.checkInInterval}
                    onChange={(e) => setConsent(prev => ({ ...prev, checkInInterval: parseInt(e.target.value) }))}
                  >
                    <option value={5}>Every 5 minutes</option>
                    <option value={10}>Every 10 minutes</option>
                    <option value={15}>Every 15 minutes</option>
                    <option value={30}>Every 30 minutes</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button className="btn btn-secondary" onClick={() => setStep(1)} style={{ flex: 1 }}>
                    Back
                  </button>
                  <button className="btn btn-primary" onClick={handleSubmit} style={{ flex: 1 }}>
                    I Consent - Start Playing
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};